import React from "react";
import { MentionsInput, Mention } from "react-mentions";
import { isEmpty } from "../hooks/constants";
import defaultStyle from "@components/MarketVisitReport/defaultStyle";
import defaultMentionStyle from "@components/MarketVisitReport/defaultMentionStyle";
import styles from "@components/MarketVisitReport/MarketVisitReport.module.css";

/**
 * DealerSection Component
 * Dealer section of Market Visit Report
 *
 * @param {Object} formData - Form data
 * @param {Function} onChange - Handler for form field changes
 * @param {Array} dealerName - Array of visited dealer names
 * @param {Array} productList - List of products for mentions
 * @param {Array} dealerList - List of dealers for mentions
 * @param {Object} characterLimitValidation - Character limit validation errors
 * @param {Object} formValidation - Form validation errors
 * @param {String} userType - Type of user
 * @param {Boolean} disable - Form disable state
 */
const DealerSection = ({
  formData,
  onChange,
  dealerName,
  productList,
  dealerList,
  characterLimitValidation,
  formValidation,
  userType,
  disable,
}) => {
  return (
    <>
      {/* Section Header */}
      <div className={`row m-0 ${styles.MarketVisitReport__headingBox}`}>
        <div className={`col-8 p-0 ${styles.MarketVisitReport__heading}`}>
          Dealer
        </div>
      </div>

      {/* Dealer Name Visited */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Name(s) of Dealer(s) visited where any issue(s) reported
          {userType !== "MH3" && userType !== "RBDM Collaborator" && (
            <span style={{ color: "red", fontSize: "1rem" }}>*</span>
          )}
        </label>
        <input
          className={` ${styles.MarketVisitReport__selectField}`}
          style={{ paddingLeft: "8px" }}
          name="dealerVisited"
          value={dealerName?.length ? dealerName : formData?.dealerVisited}
          onChange={(e) => onChange(e)}
          disabled={disable}
          maxLength={256}
        />
        {characterLimitValidation?.dealerVisited && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.dealerVisited}
          </p>
        )}
        {!formData?.dealerVisited && formValidation?.dealerVisited && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {formValidation?.dealerVisited}
          </p>
        )}
      </div>

      {/* Key Issues Faced by Dealer */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Key Observations/Issues faced by Dealer(s)
          {userType !== "MH3" && userType !== "RBDM Collaborator" && (
            <span style={{ color: "red", fontSize: "1rem" }}>*</span>
          )}
        </label>
        <MentionsInput
          value={
            isEmpty(formData?.issueFacedDealer)
              ? ""
              : formData?.issueFacedDealer
          }
          onChange={(e) => {
            onChange({
              target: {
                name: "issueFacedDealer",
                value: e.target.value,
              },
            });
          }}
          className={styles.MarketVisitRepor__input}
          style={defaultStyle}
          disabled={disable}
          maxLength={2001}
        >
          <Mention
            trigger="@p"
            data={productList}
            style={defaultMentionStyle}
          />
          <Mention trigger="@d" data={dealerList} style={defaultMentionStyle} />
        </MentionsInput>
        <p className={styles.MarketVisitReport__selectAlertMsg}>
          {" "}
          *Use @d for dealer Selection
        </p>
        {characterLimitValidation?.issueFacedDealer && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.issueFacedDealer}
          </p>
        )}
        {!formData?.issueFacedDealer && formValidation?.issueFacedDealer && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {formValidation?.issueFacedDealer}
          </p>
        )}
      </div>

      {/* Service/Delivery Feedback */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Service/Delivery related feedback
          {userType !== "MH3" && userType !== "RBDM Collaborator" && (
            <span style={{ color: "red", fontSize: "1rem" }}>*</span>
          )}
        </label>
        <MentionsInput
          value={
            isEmpty(formData?.deliveryFeedback)
              ? ""
              : formData?.deliveryFeedback
          }
          onChange={(e) => {
            onChange({
              target: {
                name: "deliveryFeedback",
                value: e.target.value,
              },
            });
          }}
          className={styles.MarketVisitRepor__input}
          style={defaultStyle}
          disabled={disable}
          maxLength={2001}
        >
          <Mention
            trigger="@p"
            data={productList}
            style={defaultMentionStyle}
          />
          <Mention trigger="@d" data={dealerList} style={defaultMentionStyle} />
        </MentionsInput>
        <p className={styles.MarketVisitReport__selectAlertMsg}>
          {" "}
          *Use @d for dealer Selection
        </p>
        {characterLimitValidation?.deliveryFeedback && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.deliveryFeedback}
          </p>
        )}
        {!formData?.deliveryFeedback && formValidation?.deliveryFeedback && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {formValidation?.deliveryFeedback}
          </p>
        )}
      </div>

      {userType !== "MH3" && userType !== "RBDM Collaborator" && (
        <>
          {/* Schemes Settlement */}
          <div className={styles.MarketVisitReport__selectableRows}>
            <label className={styles.MarketVisitReport__selectLabel}>
              Are all the schemes settled at the visited dealers by the
              respective WSS?
            </label>
            <div className={`mb-1 ${styles.myUserDetails__radioOption}`}>
              <div className="form-check">
                <input
                  className={`form-check-input ${styles.myUserDetails__option}`}
                  type="radio"
                  name="visitDealerRadio"
                  id="visitDealerRadioYes"
                  checked={
                    formData.visitDealerRadio === "yes" ||
                    formData.visitDealerRadio === "Yes"
                  }
                  value="yes"
                  onChange={(e) => onChange(e)}
                  disabled={disable}
                />
                <label
                  className={styles.marketVisitReport__radioLabel}
                  htmlFor="visitDealerRadioYes"
                >
                  Yes
                </label>
              </div>
              <div className="form-check">
                <input
                  className={`form-check-input ${styles.myUserDetails__option}`}
                  type="radio"
                  name="visitDealerRadio"
                  id="visitDealerRadioNo"
                  checked={
                    formData.visitDealerRadio === "no" ||
                    formData.visitDealerRadio === "No"
                  }
                  value="no"
                  onChange={(e) => onChange(e)}
                  disabled={disable}
                />
                <label
                  className={styles.marketVisitReport__radioLabel}
                  htmlFor="visitDealerRadioNo"
                >
                  No
                </label>
              </div>
            </div>
            {!formData?.visitDealerRadio &&
              formValidation?.visitDealerRadio && (
                <p className={styles.MarketVisitReport__validationErrors}>
                  {formValidation?.visitDealerRadio}
                </p>
              )}
          </div>

          {/* Scheme Settlement Comments */}
          <div className={styles.MarketVisitReport__selectableRows}>
            <label className={styles.MarketVisitReport__selectLabel}>
              Scheme settlement related comments
              <span style={{ color: "red", fontSize: "1rem" }}>*</span>
            </label>
            <MentionsInput
              value={
                isEmpty(formData?.schemeComments)
                  ? ""
                  : formData?.schemeComments
              }
              onChange={(e) => {
                onChange({
                  target: {
                    name: "schemeComments",
                    value: e.target.value,
                  },
                });
              }}
              className={styles.MarketVisitRepor__input}
              style={defaultStyle}
              disabled={disable}
              maxLength={501}
            >
              <Mention
                trigger="@p"
                data={productList}
                style={defaultMentionStyle}
              />
              <Mention
                trigger="@d"
                data={dealerList}
                style={defaultMentionStyle}
              />
            </MentionsInput>
            <p className={styles.MarketVisitReport__selectAlertMsg}>
              {" "}
              *Use @d for dealer Selection
            </p>
            {characterLimitValidation?.schemeComments && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {characterLimitValidation?.schemeComments}
              </p>
            )}
            {!formData?.schemeComments && formValidation?.schemeComments && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {formValidation?.schemeComments}
              </p>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default DealerSection;
